import { AccountConfirmation } from '@/features/growgen/AccountConfirmation';
// © 2025 Camp Promethean, LLC. All Rights Reserved.

import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { Spinner } from './ui/spinner';

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * AdminRoute - A component that wraps routes that should only be accessible to admins
 * Redirects to home page if the user is not an admin
 * Shows a loading spinner while checking admin status
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAdmin, loading, error } = useIsAdmin();

  // Show loading spinner while checking admin status
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <Spinner size="lg" />
        <span className="ml-3 text-lg">Checking permissions...</span>
      </div>
    );
  }

  // Redirect to home page if there was an error or user is not an admin
  if (error || !isAdmin) {
    console.error('Access denied: User is not an admin', error);
    return <Navigate to="/" replace />;
  }

  // Render the protected route content if user is an admin
  return <AccountConfirmation>{children}</AccountConfirmation>;
};

export default AdminRoute; 